import { useViewport } from "src/contexts";
import { DebugText, Icon, TooltipV2 } from "~components";
import { Css } from "~generated/css";
import { Maybe } from "~generated/graphqlTypes";
import { useTestIds } from "~utils";
import { OptionPreviewImage } from "./OptionPreviewImage";

type OptionThumbnailProps = {
  optionName: string;
  /** Should be optimized for the tooltip preview image 155x155; */
  imageUrl: Maybe<string>;
  /** Must be be unique for tooltip to function properly */
  thumbnailId: Maybe<string>;
  onClick: () => void;
  isSelected?: boolean;
  disabled?: boolean;
  size?: number;
  debugText?: string;
};

/**
 * Wraps an Option around an OptionPreviewImage at 40x40 size with a checkmark overlay if selected.
 * Displays a tooltip with option name and larger image when hovered.
 */
export function OptionThumbnail(props: OptionThumbnailProps) {
  const {
    disabled = false,
    onClick,
    isSelected = false,
    optionName,
    imageUrl,
    thumbnailId,
    size = 40,
    debugText,
  } = props;
  const { isDesktop } = useViewport();
  const tid = useTestIds(props, "optionImage");
  const anchorId = thumbnailId?.replaceAll(":", "");

  return (
    <div
      id={anchorId}
      css={{
        ...Css.oh.relative.ba.br8.sqPx(size).fs0.df.aic.jcc.$,
        ...Css.if(disabled).cursorNotAllowed.o25.$,
        ...Css.if(!disabled).cursorPointer.if(!imageUrl).$,
        ...Css.if(!!isSelected).bcBlack.else.bcGray300.$,
      }}
      onClick={!disabled ? onClick : undefined}
      {...tid}
    >
      {isSelected && <SelectedOverlay />}
      <OptionPreviewImage imageUrl={imageUrl} optionName={optionName} size={size} />
      <TooltipV2
        cssOverrides={
          Css.df.fdc.aic.jcc.bgWhite.boxShadow("-4px -4px 10px rgba(0, 0, 0, 0.1), 4px 4px 10px rgba(0, 0, 0, 0.1)").$
        }
        positionStrategy="fixed"
        anchorSelect={`#${anchorId}`}
        noArrow={false}
        hidden={!isDesktop}
      >
        <DebugText>{debugText}</DebugText>
        <OptionTipContent imgSrc={imageUrl} name={optionName} />
      </TooltipV2>
    </div>
  );
}

function SelectedOverlay() {
  return (
    <div
      // NOTE:
      // - br(7px) prevents a sliver of the option image from showing between this overlay's white border and the parent's black border
      // - Manually setting background opacity keeps white border opaque
      css={
        Css.absolute.w100.h100.top0.right0.bottom0.left0.df.aic.jcc.bgColor("rgba(0, 0, 0, 0.50)").borderRadius("7px")
          .ba.bcWhite.$
      }
    >
      <div css={Css.df.aic.jcc.relative.z2.$}>
        <Icon name="checkmark-white-large" />
      </div>
    </div>
  );
}

// Will always show the name at a minimum
function OptionTipContent({ imgSrc, name }: { imgSrc: Maybe<string>; name: string }) {
  return (
    <div css={Css.df.fdc.gap(1.5).aic.$}>
      <span css={Css.t14.gray800.lineClamp3.$}>{name}</span>
      {imgSrc && <OptionPreviewImage imageUrl={imgSrc} optionName={name} size={155} />}
    </div>
  );
}

/** Loading placeholder for option thumbnails */
export function OptionThumbnailLoader({ size = 40 }: { size?: number }) {
  return (
    <div
      css={
        Css.oh.ba.br8.sqPx(size).bcGray300.bgGray200.add("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
          .$
      }
    />
  );
}
